import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { useAuth0 } from '@auth0/auth0-react';

import { providerActions } from '../../../../store';
import { ProviderClinicalStatus } from '../../../constants/general';
import { toRoleStrings } from '../utils';
import { useProviderDetailsState } from './useProviderDetailsState';

import type { AppDispatch } from '../../../../store';
import type { ISelectOption } from '../../../../utils/types';

export function useProviderPromoteActions() {
  const dispatch = useDispatch<AppDispatch>();
  const { getAccessTokenSilently } = useAuth0();
  const { provider } = useProviderDetailsState();
  const [showPromoteToClinical, setShowPromoteToClinical] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState<ISelectOption[]>([]);

  useEffect(() => {
    setShowPromoteToClinical(false);
    setSelectedRoles([]);
  }, [provider?.id]);

  const handlePromoteProvider = async (onSuccess?: () => void) => {
    if (!provider) return;
    if (!selectedRoles?.length && provider.clinical_status !== ProviderClinicalStatus.ON_HOLD) {
      toast.error('At least one role must be selected');
      return;
    }
    const data =
      provider.clinical_status === ProviderClinicalStatus.ON_HOLD
        ? {}
        : { roles: toRoleStrings(selectedRoles) };
    const res = await dispatch(
      providerActions.promoteProvider({
        providerId: provider.id,
        data,
        getAccessTokenSilently,
      }),
    );
    if (!res.error) {
      setShowPromoteToClinical(false);
      setSelectedRoles([]);
      onSuccess?.();
      dispatch(providerActions.updateProviderLists(res.payload));
    }
  };

  const handlePromoteToClinicalClick = (onSuccess?: () => void) => {
    if (provider?.clinical_status === ProviderClinicalStatus.ON_HOLD) {
      handlePromoteProvider(onSuccess);
    } else {
      setShowPromoteToClinical(true);
    }
  };

  const closePromoteDialog = () => {
    setShowPromoteToClinical(false);
    setSelectedRoles([]);
  };

  return {
    showPromoteToClinical,
    selectedRoles,
    setSelectedRoles,
    handlePromoteProvider,
    handlePromoteToClinicalClick,
    closePromoteDialog,
  };
}
